import React, { useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileCsv, faFileExport } from '@fortawesome/sharp-regular-svg-icons'
import {
  ActionIcon,
  Button,
  Checkbox,
  Grid,
  Group,
  Modal,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import type { DefaultPageExportProps } from './default-page-export'
import {
  generateCsvBlobFromString,
  generateCsvStringFromExportItem,
  generateExportItem,
  getDefaultCheckedExportColumns,
  getValidExportColumnIds,
} from './page-export-utils'

const DefaultPageExport = <T,>({
  tableInstance,
  actionIconSize = 'lg',
}: DefaultPageExportProps<T>): React.ReactElement => {
  const [exportLoading, setExportLoading] = useState(false)

  const [columnsToExport, setColumnsToExport] = useState(() =>
    getDefaultCheckedExportColumns({ tableInstance }),
  )
  const [opened, { open, close }] = useDisclosure()
  const theme = useMantineTheme()

  const validExportColumnIds = getValidExportColumnIds({ tableInstance })

  const checkBoxOptions = validExportColumnIds.map((columnId) => {
    const column = tableInstance.getColumn(columnId)

    if (!column) {
      return null
    }
    return {
      value: columnId,
      label:
        typeof column.columnDef.header === 'string'
          ? column?.columnDef.header
          : columnId,
    }
  })

  const handleExport = (): void => {
    setExportLoading(true)

    const exportItem = generateExportItem<T>({
      tableInstance,
      enabledExportColumns: validExportColumnIds.filter(
        (columnId) => columnsToExport[columnId] === true,
      ),
    })

    const csvString = generateCsvStringFromExportItem({ exportItem })

    const blob = generateCsvBlobFromString({ csvString })

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'export.csv'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)
    setExportLoading(false)
    close()
  }
  return (
    <>
      <Modal
        size="lg"
        opened={opened}
        onClose={close}
        //title={t('export_modal_title')}
        title={i18n.t('export_modal_title', 'Export')}
      >
        <Grid>
          <Grid.Col>
            <Text>
              {/* {   t('choose_columns_to_export')} */}
              {i18n.t(
                'choose_columns_to_export',
                'Welche Spalten sollen exportiert werden?',
              )}
            </Text>
          </Grid.Col>
          <Grid.Col>
            <Stack>
              {checkBoxOptions.map((option) => {
                return option ? (
                  <Checkbox
                    disabled={exportLoading}
                    label={option.label}
                    key={`checkbox-${option.value}`}
                    checked={columnsToExport[option.value]}
                    onChange={(event) => {
                      const isChecked = event.currentTarget.checked
                      setColumnsToExport((c) => {
                        return {
                          ...c,
                          [option.value]: isChecked,
                        }
                      })
                    }}
                  />
                ) : null
              })}
            </Stack>
          </Grid.Col>

          <Grid.Col>
            <Group position="right">
              <Button
                loaderPosition="left"
                loading={exportLoading}
                onClick={() => handleExport()}
                disabled={
                  !Object.values(columnsToExport).some(
                    (value) => value === true,
                  )
                }
                leftIcon={<FontAwesomeIcon icon={faFileCsv} />}
              >
                {/* {t('export_as_csv')} */}
                {i18n.t('export_as_csv', 'Als CSV exportieren')}
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      </Modal>
      <Tooltip
        //label={t('export_current_page')}
        label={i18n.t('export_current_page', 'Aktive Seite exportieren')}
      >
        <ActionIcon
          disabled={checkBoxOptions.length === 0}
          onClick={open}
          variant="filled"
          size={actionIconSize}
          color={theme.primaryColor}
        >
          <FontAwesomeIcon
            icon={faFileExport}
            fontSize={theme.fontSizes[actionIconSize]}
          />
        </ActionIcon>
      </Tooltip>
    </>
  )
}

export default DefaultPageExport
